import { RefreshCw } from 'lucide-react'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { formatCurrency, formatDate } from '@/lib/format'
import type { Company } from '@/types/api'

interface FinancialSnapshotProps {
  company: Company
}

export function FinancialSnapshot({ company }: FinancialSnapshotProps) {
  const { financials, financialsUpdatedAt } = company

  if (!financials) {
    return (
      <div className="space-y-2 border-b border-primary/10 pb-3">
        <Typography variant="h6" className="mb-3.5">
          Financial Snapshot
        </Typography>
        <Typography variant="muted" className="text-xs text-primary/70">
          No financial data
        </Typography>
      </div>
    )
  }

  const metrics = [
    { label: 'TTM Revenue', value: financials.ltmRevenue },
    { label: 'TTM EBITDA', value: financials.ltmEbitda },
    { label: 'TTM Gross', value: financials.ltmGrossProfit },
    { label: 'TTM Net Income', value: financials.ltmNetIncome },
  ]

  return (
    <div className="border-b border-primary/10 pb-3">
      <Typography variant="h6" className="mb-3.5">
        Financial Snapshot
      </Typography>

      <div className="mb-4 grid grid-cols-4 gap-1">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="flex flex-col justify-between rounded-md border border-primary/20 bg-white/50 pl-3 pr-2 py-2"
          >
            <Typography variant="body-sm" className="text-primary/80">
              {metric.label}
            </Typography>
            <Typography
              variant="body"
              className={cn(
                'tabular-nums',
                metric.value != null && metric.value < 0
                  ? 'text-destructive'
                  : 'text-primary'
              )}
            >
              {formatCurrency(metric.value)}
            </Typography>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <div className="flex h-[22px] items-center gap-1 rounded-sm bg-primary/5 px-2 py-0.5 text-xs text-primary/70">
          <RefreshCw className="h-3 w-3" />
          <Typography variant="body-sm" as="span" className="text-primary/70">
            As of:{' '}
            {financials.ltmPeriodEndDate
              ? formatDate(financials.ltmPeriodEndDate)
              : '—'}
          </Typography>
        </div>
        <div className="flex h-[22px] items-center rounded-sm bg-primary/5 px-2 py-0.5 text-xs text-primary/70">
          <Typography variant="body-sm" as="span" className="text-primary/70">
            Last update:{' '}
            {financialsUpdatedAt ? formatDate(financialsUpdatedAt) : '—'}
          </Typography>
        </div>
      </div>
    </div>
  )
}
